import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ScanTypeCard from "../ScanTypeCard";
import { useNewScanWizard } from "../NewScanWizardProvider";
import { fetchToolCatalog } from "../../../api/scans";

const SelectScansStep = () => {
  const navigate = useNavigate();
  const { selectedToolIds, setSelectedToolIds } = useNewScanWizard();
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const loadTools = async () => {
      try {
        setLoading(true);
        const data = await fetchToolCatalog(false); // Only enabled tools
        setTools(data);
        
      } catch (err) {
        setError("Failed to load scan tools");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    loadTools();
  }, []);

  const allToolsSelected = tools.length > 0 && tools.every(tool => selectedToolIds.includes(tool.id));

  const handleToggleTool = (toolId) => {
    setSelectedToolIds(prev => 
      prev.includes(toolId)
        ? prev.filter(id => id !== toolId)
        : [...prev, toolId]
    );
  };

  const handleSelectAll = () => {
    if (allToolsSelected) {
      setSelectedToolIds([]);
    } else {
      setSelectedToolIds(tools.map(t => t.id));
    }
  };

  const handleNext = () => {
    if (selectedToolIds.length === 0) {
      setError("Please select at least one scan tool");
      return;
    }
    navigate("/scans/new/targets");
  };

  if (loading) {
    return <div className="step-content-loading">Loading scan tools...</div>;
  }

  if (error && !tools.length) {
    return <div className="step-content-error">{error}</div>;
  }

  // Group tools by category
  const toolsByCategory = tools.reduce((acc, tool) => {
    const category = tool.category || "Other";
    if (!acc[category]) acc[category] = [];
    acc[category].push(tool);
    return acc;
  }, {});

  const categoryOrder = [
    "Network Scanning & Enumeration",
    "Web Vulnerability Scanners",
    "SSL / Security Headers",
    "Vulnerability Intelligence",
  ];

  const sortedCategories = categoryOrder.filter(cat => toolsByCategory[cat]);

  return (
    <div className="select-scans-step">
      <h2>Step 1: Select Scans</h2>
      <p className="step-description">Choose which security scanning tools to run against your targets.</p>

      {error && <div className="error-banner">{error}</div>}

      <div className="select-all-control">
        <label>
          <input
            type="checkbox"
            checked={allToolsSelected}
            onChange={handleSelectAll}
          />
          <strong>Select All Tools</strong>
        </label>
      </div>

      <div className="tools-container">
        {sortedCategories.map(category => (
          <div key={category} className="tool-category">
            <h3 className="category-heading">{category}</h3>
            <div className="tools-grid">
              {toolsByCategory[category].map(tool => (
                <ScanTypeCard
                  key={tool.id}
                  tool={tool}
                  isSelected={selectedToolIds.includes(tool.id)}
                  onToggle={handleToggleTool}
                  notConfigured={tool.status === "not_configured"}
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="step-actions">
        <button
          type="button"
          className="primary-button"
          onClick={handleNext}
          disabled={selectedToolIds.length === 0}
        >
          Next →
        </button>
      </div>
    </div>
  );
};

export default SelectScansStep;
